import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import {
    selectedJobAtom, selectedTeamAtom, selectedVersionAtom, dataSourceVersionAtom, procurementScopeAtom,
} from '../store/company';
import { loadUncommittedRollup, fNum } from '../db/company';
import ProcurementScope, { scopeRequest, scopeLabel, latestVersionGuid } from './procurementScope';
import {
    Chip, FilterDropdown, OutlineButton, SkeletonRows, SkeletonStat,
} from './procurementFilters';
import {
    rollupBy, totals,
    etcLines, poAmountMap, filterLines, sortLines, lineFilterOptions, lineTotal,
} from './uncommittedData';
import { describeCostType } from '../helpers/procurementCategories';

// Uncommitted tab.
//
//   Uncommitted = ETC - Open Commitments
//
// Forecast money nobody has cut a PO for, across a team, several teams, or the
// whole company. The rollup runs in SQL (readUncommittedRollup.sql) because the
// open-PO view holds millions of rows.
//
// The three sections below sit at three different grains on purpose — ETC exists
// per (job, cost type), resource codes only exist on PO lines. The "How this is
// calculated" panel spells that out rather than leaving the reader to guess why
// resource rows don't add up to the headline.

const GROUPINGS = [
    { value: 'Category', label: 'Category' },
    { value: 'Cost_Type_Code', label: 'Cost type' },
    { value: 'Job_Number', label: 'Job' },
];

// The line table. Every column is sortable, so the header cells are declared
// once here rather than hand-written twice (header + body). Columns share the
// available width by `flex` weight, never shrinking below `min` — on a narrow
// window the surrounding horizontal ScrollView takes over.
const LINE_COLUMNS = [
    { key: 'jobNumber', label: 'PROJECT', flex: 1.6, min: 150 },
    { key: 'poNumber', label: 'PO NUMBER', flex: 0.8, min: 90 },
    { key: 'notes', label: 'NOTES', flex: 1.4, min: 110 },
    { key: 'resourceCode', label: 'RESOURCE CODE', flex: 1, min: 110 },
    { key: 'supplierName', label: 'SUPPLIER NAME', flex: 1.5, min: 140 },
    { key: 'costTypeCode', label: 'COST TYPE', flex: 1.4, min: 130 },
    { key: 'costCode', label: 'COST CODE', flex: 1.3, min: 120 },
    { key: 'phaseStart', label: 'PHASE START', flex: 0.8, min: 90 },
    { key: 'phaseEnd', label: 'PHASE END', flex: 0.8, min: 90 },
    { key: 'currentPo', label: 'CURRENT PO', flex: 1, min: 110, right: true },
    { key: 'etc', label: 'ETC AMOUNT', flex: 1, min: 110, right: true },
];

// A fluid table cell: grows by weight, shrinks down to `min`, then the table
// scrolls horizontally instead of squashing further.
const col = (flex, min, right) => ({
    flexGrow: flex, flexShrink: 1, flexBasis: 0, minWidth: min,
    ...(right ? { textAlign: 'right' } : null),
});

const PO_FILTERS = [
    { value: 'all', label: 'All lines' },
    { value: 'without', label: 'No PO' },
    { value: 'with', label: 'Has PO' },
];

const card = {
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 18,
    paddingRight: 18,
    marginBottom: 16,
};

const th = {
    fontSize: 11,
    fontWeight: '700',
    color: '#475569',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
};

const td = {
    fontSize: 12,
    color: '#1e293b',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
};

const money = (n) => (n === null || n === undefined ? '' : fNum(Math.round(n)));

// SQL dates are naive datetimes stored in server-local time — format in UTC so
// they don't shift a day when rendered in another timezone.
const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');

const Stat = ({ label, value, tone }) => (
    <View style={{ marginRight: 28 }}>
        <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>{label}</Text>
        <Text style={{ fontSize: 20, fontWeight: '700', color: tone || '#0f172a' }}>{value}</Text>
    </View>
);

// Rollup table column widths — header, rows, and loading skeleton all read
// from here so they line up.
const ROLLUP_COLS = [240, 140, 160, 150];
const ROLLUP_SKELETON = ROLLUP_COLS.map((w) => ({ width: w }));
const LINE_SKELETON = LINE_COLUMNS.map((c) => col(c.flex, c.min));

const Uncommitted = () => {
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    // The version picked in the top menu; live version when none is picked.
    const forecastGuid = useMemo(
        () => selectedVersion || latestVersionGuid(versions),
        [selectedVersion, versions]
    );
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [scope, setScope] = useAtom(procurementScopeAtom);

    const [data, setData] = useState({ data: [], detail: [], outliers: [], lines: [], poAmounts: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [grouping, setGrouping] = useState('Category');
    const [showMethod, setShowMethod] = useState(false);
    const [loadedLabel, setLoadedLabel] = useState('');

    // Line-table filters and sort. Default sort is largest ETC first — the
    // biggest uncovered numbers are what anyone opens this view to find.
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);
    const [poFilter, setPoFilter] = useState('all');
    const [sortKey, setSortKey] = useState('etc');
    const [sortDir, setSortDir] = useState('desc');
    const [showAllLines, setShowAllLines] = useState(false);

    const toggleIn = (list, setList, value) =>
        setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

    // Clicking a column re-sorts by it; clicking the active column flips it.
    const onSort = (key) => {
        if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortKey(key); setSortDir(key === 'etc' ? 'desc' : 'asc'); }
    };

    // Default scope: the job picked in the top menu, else its team. Both chips
    // mirror the top menu, so whichever mode is active follows the top-menu
    // selection as it changes.
    useEffect(() => {
        if (!scope || scope.mode === 'company') {
            if (selectedJob) setScope({ mode: 'job', job: selectedJob });
            else setScope({ mode: 'teams', teams: selectedTeam ? [selectedTeam] : [] });
        } else if (scope.mode === 'job' && selectedJob && scope.job !== selectedJob) {
            setScope({ mode: 'job', job: selectedJob });
        } else if (scope.mode === 'teams') {
            const teams = selectedTeam ? [selectedTeam] : [];
            if ((scope.teams || []).join() !== teams.join()) setScope({ mode: 'teams', teams });
        }
    }, [selectedJob, selectedTeam, scope, setScope]);

    const load = useCallback(async () => {
        if (!forecastGuid) return;
        if (scope?.mode === 'job' && !scope.job) return;
        if (scope?.mode !== 'job' && !(scope?.teams || []).length) {
            setError('Pick a team in the top menu.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await loadUncommittedRollup(scopeRequest(scope, forecastGuid));
            if (res?.error) {
                setError(String(res.error));
                setData({ data: [], detail: [], outliers: [], lines: [], poAmounts: [] });
            } else {
                setData({
                    data: res?.data || [],
                    detail: res?.detail || [],
                    outliers: res?.outliers || [],
                    lines: res?.lines || [],
                    poAmounts: res?.poAmounts || [],
                });
                setLoadedLabel(scopeLabel(scope));
            }
        } catch (e) {
            setError(e?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [scope, forecastGuid]);

    // Run without a click — on open, and again whenever the scope or the live
    // version moves. Debounced so rapid team-chip clicks collapse into one query.
    useEffect(() => {
        const t = setTimeout(load, 400);
        return () => clearTimeout(t);
    }, [load]);

    const t = useMemo(() => totals(data.data, data.detail, data.outliers), [data]);
    const grouped = useMemo(() => rollupBy(data.data, grouping), [data.data, grouping]);

    const poMap = useMemo(() => poAmountMap(data.poAmounts), [data.poAmounts]);
    const allLines = useMemo(() => etcLines(data.lines, poMap), [data.lines, poMap]);
    const lineOptions = useMemo(() => lineFilterOptions(allLines), [allLines]);
    const visibleLines = useMemo(
        () => sortLines(
            filterLines(allLines, {
                search, categories: catFilter, costTypes: typeFilter, po: poFilter,
            }),
            sortKey,
            sortDir,
        ),
        [allLines, search, catFilter, typeFilter, poFilter, sortKey, sortDir],
    );
    const shownLines = showAllLines ? visibleLines : visibleLines.slice(0, 250);
    const visibleTotal = useMemo(() => lineTotal(visibleLines), [visibleLines]);

    // Excel export of the rollup table exactly as grouped on screen, with the
    // headline totals as a final row.
    const exportRollup = useCallback(() => {
        const dim = GROUPINGS.find((g) => g.value === grouping)?.label || grouping;
        const rows = grouped.map((r) => ({
            [dim]: grouping === 'Cost_Type_Code' ? `${r.key} ${describeCostType(r.key)}` : r.key,
            ETC: r.etc,
            'Open Commitments': r.openCommitted,
            Uncommitted: r.uncommitted,
        }));
        rows.push({
            [dim]: 'Total',
            ETC: t.etc,
            'Open Commitments': t.openCommitted,
            Uncommitted: t.uncommitted,
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Uncommitted');
        const stamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `Uncommitted_${stamp}.xlsx`);
    }, [grouped, grouping, t]);

    // Excel export of the line table exactly as filtered and sorted on screen.
    const exportLines = useCallback(() => {
        const rows = visibleLines.map((r) => ({
            'Job Number': r.jobNumber,
            'Job Name': r.jobName,
            'PO Number': r.poNumber || '',
            Notes: r.notes || '',
            'Resource Code': r.resourceCode || '',
            'Supplier Name': r.supplierName || '',
            'Cost Type': r.costTypeCode,
            'Cost Type Label': r.costTypeLabel || '',
            'Burn Start': fDate(r.typeStart),
            'Burn End': fDate(r.typeEnd),
            'Cost Code': r.costCode || '',
            'Cost Code Label': r.costCodeLabel || '',
            'Phase Start': fDate(r.phaseStart),
            'Phase End': fDate(r.phaseEnd),
            'Current PO Amount': r.currentPo === null ? '' : r.currentPo,
            'ETC Amount': r.etc,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ETC Lines');
        const stamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `Uncommitted_ETC_Lines_${stamp}.xlsx`);
    }, [visibleLines]);

    return (
        <ScrollView style={{ flex: 1, backgroundColor: '#f8fafc' }}>
            <View style={{ paddingTop: 22, paddingBottom: 40, paddingLeft: 26, paddingRight: 26 }}>
                <Text style={{ fontSize: 22, fontWeight: '700', color: '#0f172a', marginBottom: 4 }}>Uncommitted</Text>
                <Text style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
                    Forecast money with no purchase order behind it. Uncommitted = ETC &minus; Open Commitments.
                </Text>

                <View style={card}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        <ProcurementScope scope={scope} onChange={setScope} job={selectedJob} team={selectedTeam} />
                        <View style={{ flexGrow: 1 }} />
                        <Text style={{ fontSize: 12, color: '#64748b', marginRight: 10, marginBottom: 6 }}>
                            {loading ? 'Loading...'
                                : (loadedLabel ? `Showing: ${loadedLabel} · ${t.jobs} jobs` : '')}
                        </Text>
                        <OutlineButton label="Refresh" onPress={load} disabled={loading} />
                    </View>
                    {error ? (
                        <Text style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{error}</Text>
                    ) : null}
                </View>

                <View style={card}>
                    {loading && !data.data.length ? (
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 }}>
                            {['ETC', 'OPEN', 'UNCOMMITTED'].map((k) => <SkeletonStat key={k} />)}
                        </View>
                    ) : (
                    <>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 }}>
                                <Stat label="ETC" value={money(t.etc)} />
                                <Stat label="OPEN COMMITMENTS" value={money(t.openCommitted)} />
                                <Stat
                                    label="UNCOMMITTED"
                                    value={money(t.uncommitted)}
                                    tone={t.uncommitted < 0 ? '#b91c1c' : '#166534'}
                                />
                            </View>
                            <Text style={{ fontSize: 11, color: '#64748b' }}>
                                A negative figure means more is committed than the remaining forecast covers.
                            </Text>
                            <Pressable onPress={() => setShowMethod((s) => !s)} style={{ marginTop: 10 }}>
                                <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                                    {showMethod ? '- Hide' : '+ How this is calculated'}
                                </Text>
                            </Pressable>

                            {showMethod ? (
                                <View style={{ marginTop: 10, borderTopWidth: 1, borderColor: '#e2e8f0', paddingTop: 10 }}>
                                    <Text style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                        ETC comes from the Materials, Rentals, Subcontractors and Other tabs
                                        (Forecast_User_Data, field &ldquo;etc&rdquo;). Open Commitments are
                                        PO_True_Open_Amount on the open PO view. They are matched per job and cost
                                        type — the only grain where both reliably exist.
                                    </Text>
                                    <Text style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                        Resource codes are resolved from the PO&rsquo;s equipment tier through the
                                        global Resource Code Mapping. Only equipment rentals carry a tier, so
                                        materials, subs and services legitimately have none.
                                        Resolved on {t.resourceRowsResolved} of {t.resourceRowsTotal} commitment rows.
                                    </Text>
                                    <Text style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                        ETC is not shown per resource code: only 40 of ~1.2M ETC rows carry one, so
                                        any split across resources would be invented rather than measured.
                                    </Text>
                                    {t.nonNumericEtcRows ? (
                                        <Text style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>
                                            {t.nonNumericEtcRows} ETC cells hold text the forecast tabs do not
                                            count, so they contribute nothing to the total here either.
                                        </Text>
                                    ) : null}
                                    {t.largestEtcEntries.length ? (
                                        <View style={{ marginTop: 6 }}>
                                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#334155', marginBottom: 4 }}>
                                                Largest single ETC entries — worth a look, the field is free text and
                                                PO numbers occasionally get typed into it:
                                            </Text>
                                            {t.largestEtcEntries.slice(0, 5).map((o, i) => (
                                                <Text key={i} style={{ fontSize: 11, color: '#475569' }}>
                                                    {o.jobNumber} &middot; {o.tab} &middot; {o.label} — {money(o.etc)}
                                                </Text>
                                            ))}
                                        </View>
                                    ) : null}
                                </View>
                            ) : null}
                    </>
                    )}
                </View>

                <View style={card}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginRight: 14, marginBottom: 6 }}>
                            Uncommitted by
                        </Text>
                        {GROUPINGS.map((g) => (
                            <Chip
                                key={g.value}
                                label={g.label}
                                active={grouping === g.value}
                                onPress={() => setGrouping(g.value)}
                            />
                        ))}
                        <View style={{ flexGrow: 1 }} />
                        <OutlineButton label="Export to Excel" onPress={exportRollup} disabled={!grouped.length} />
                    </View>

                    <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                        <Text style={[th, { width: ROLLUP_COLS[0] }]}>{GROUPINGS.find((g) => g.value === grouping)?.label}</Text>
                        <Text style={[th, { width: ROLLUP_COLS[1], textAlign: 'right' }]}>ETC</Text>
                        <Text style={[th, { width: ROLLUP_COLS[2], textAlign: 'right' }]}>OPEN COMMITMENTS</Text>
                        <Text style={[th, { width: ROLLUP_COLS[3], textAlign: 'right' }]}>UNCOMMITTED</Text>
                    </View>
                    {loading && !grouped.length ? (
                        <SkeletonRows cells={ROLLUP_SKELETON} rows={5} />
                    ) : null}
                    {grouped.map((r) => (
                        <View
                            key={r.key}
                            style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}
                        >
                            <Text style={[td, { width: ROLLUP_COLS[0] }]}>
                                {grouping === 'Cost_Type_Code' ? `${r.key} ${describeCostType(r.key)}` : r.key}
                            </Text>
                            <Text style={[td, { width: ROLLUP_COLS[1], textAlign: 'right' }]}>{money(r.etc)}</Text>
                            <Text style={[td, { width: ROLLUP_COLS[2], textAlign: 'right' }]}>{money(r.openCommitted)}</Text>
                            <Text style={[td, {
                                width: ROLLUP_COLS[3], textAlign: 'right', fontWeight: '700',
                                color: r.uncommitted < 0 ? '#b91c1c' : '#166534',
                            }]}>
                                {money(r.uncommitted)}
                            </Text>
                        </View>
                    ))}
                    {!loading && !grouped.length ? (
                        <Text style={{ fontSize: 13, color: '#64748b', paddingTop: 14, paddingBottom: 6 }}>
                            {error ? 'Nothing loaded.' : 'No forecast rows in this scope.'}
                        </Text>
                    ) : null}
                </View>

                <View style={card}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a', marginBottom: 2 }}>
                        ETC lines
                    </Text>
                            <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                                Every forecast line carrying an ETC amount. These sum back exactly to the
                                ETC figure above — this is the ETC side on its own, with no commitment
                                join, so no row is repeated.
                            </Text>

                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, zIndex: 5 }}>
                                <TextInput
                                    value={search}
                                    onChangeText={setSearch}
                                    placeholder="Search job, supplier, PO, notes, cost code..."
                                    style={{
                                        width: 340, fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                        paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                                        marginRight: 10, marginBottom: 6,
                                    }}
                                />
                                {PO_FILTERS.map((f) => (
                                    <Chip
                                        key={f.value}
                                        label={f.label}
                                        active={poFilter === f.value}
                                        onPress={() => setPoFilter(f.value)}
                                    />
                                ))}
                                <View style={{ width: 6 }} />
                                <FilterDropdown
                                    label="Category"
                                    options={lineOptions.categories}
                                    selected={catFilter}
                                    onToggle={(v) => toggleIn(catFilter, setCatFilter, v)}
                                    onClear={() => setCatFilter([])}
                                />
                                <FilterDropdown
                                    label="Cost type"
                                    options={lineOptions.costTypes}
                                    selected={typeFilter}
                                    onToggle={(v) => toggleIn(typeFilter, setTypeFilter, v)}
                                    onClear={() => setTypeFilter([])}
                                    renderLabel={(o) => `${o.value} ${describeCostType(o.value)}`}
                                />
                            </View>

                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                                <Text style={{ fontSize: 12, color: '#334155' }}>
                                    {visibleLines.length} of {allLines.length} lines &middot; {money(visibleTotal)} ETC
                                </Text>
                                {(search || catFilter.length || typeFilter.length || poFilter !== 'all') ? (
                                    <Pressable
                                        onPress={() => {
                                            setSearch(''); setCatFilter([]); setTypeFilter([]); setPoFilter('all');
                                        }}
                                        style={{ marginLeft: 12 }}
                                    >
                                        <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>Clear filters</Text>
                                    </Pressable>
                                ) : null}
                                <View style={{ flexGrow: 1 }} />
                                <OutlineButton label="Export to Excel" onPress={exportLines} disabled={!visibleLines.length} />
                            </View>

                            <ScrollView horizontal contentContainerStyle={{ minWidth: '100%' }}>
                                <View style={{ flexGrow: 1 }}>
                                    <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                        {LINE_COLUMNS.map((c) => (
                                            <Pressable key={c.key} onPress={() => onSort(c.key)} style={col(c.flex, c.min)}>
                                                <Text style={[th, {
                                                    textAlign: c.right ? 'right' : 'left',
                                                    color: sortKey === c.key ? '#0f172a' : '#475569',
                                                }]}>
                                                    {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ^' : ' v') : ''}
                                                </Text>
                                            </Pressable>
                                        ))}
                                    </View>
                                    {loading && !allLines.length ? (
                                        <SkeletonRows cells={LINE_SKELETON} rows={6} />
                                    ) : null}
                                    {shownLines.map((r) => (
                                        <View key={r.id} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                            <View style={[col(1.6, 150), { paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }]}>
                                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{r.jobNumber}</Text>
                                                <Text style={{ fontSize: 11, color: '#64748b' }}>{r.jobName}</Text>
                                            </View>
                                            <Text style={[td, col(0.8, 90)]}>{r.poNumber || '—'}</Text>
                                            <Text style={[td, col(1.4, 110), { color: '#475569' }]}>{r.notes}</Text>
                                            <Text style={[td, col(1, 110)]}>{r.resourceCode || '—'}</Text>
                                            <Text style={[td, col(1.5, 140)]}>{r.supplierName || '—'}</Text>
                                            <View style={[col(1.4, 130), { paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }]}>
                                                <Text style={{ fontSize: 12, color: '#1e293b' }}>
                                                    {r.costTypeCode}{r.costTypeLabel && r.costTypeLabel !== r.costTypeCode ? ` - ${r.costTypeLabel}` : ''}
                                                </Text>
                                                {r.typeStart || r.typeEnd ? (
                                                    <Text style={{ fontSize: 11, color: '#64748b' }}>
                                                        {fDate(r.typeStart) || '?'} – {fDate(r.typeEnd) || '?'}
                                                    </Text>
                                                ) : null}
                                            </View>
                                            <Text style={[td, col(1.3, 120)]}>
                                                {r.costCode ? `${r.costCode}${r.costCodeLabel ? ` - ${r.costCodeLabel}` : ''}` : '—'}
                                            </Text>
                                            <Text style={[td, col(0.8, 90)]}>{fDate(r.phaseStart) || '—'}</Text>
                                            <Text style={[td, col(0.8, 90)]}>{fDate(r.phaseEnd) || '—'}</Text>
                                            <Text style={[td, col(1, 110, true), { color: '#475569' }]}>
                                                {r.currentPo === null ? '—' : money(r.currentPo)}
                                            </Text>
                                            <Text style={[td, col(1, 110, true), { fontWeight: '600' }]}>{money(r.etc)}</Text>
                                        </View>
                                    ))}
                                </View>
                            </ScrollView>

                            {visibleLines.length > shownLines.length ? (
                                <Pressable onPress={() => setShowAllLines(true)} style={{ marginTop: 10 }}>
                                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                                        Show all {visibleLines.length} lines
                                    </Text>
                                </Pressable>
                            ) : null}
                            {!loading && !visibleLines.length ? (
                                <Text style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                                    {allLines.length ? 'No lines match these filters.' : 'No ETC lines in this scope.'}
                                </Text>
                            ) : null}
                        </View>
            </View>
        </ScrollView>
    );
};

export default Uncommitted;
